import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 25;

const getToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('adminSession'))?.token || null;
  } catch (e) {
    return null;
  }
};
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const errText = (err, fallback) => err?.response?.data?.message || fallback;
const notifySidebar = () => window.dispatchEvent(new Event('registrations-changed'));

function formatDate(v) {
  if (!v) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function courseText(r) {
  const list = (r.courses || []).filter((c) => c && c !== 'NA');
  const other = r.course_other_text ? ` (${r.course_other_text})` : '';
  return list.length ? list.join(', ') + other : r.course_other_text || '—';
}

function Field({ label, value, wide }) {
  const empty = value === null || value === undefined || String(value).trim() === '';
  return (
    <div className={wide ? 'col-12' : 'col-md-6'}>
      <div className="text-12 text-gray-500">{label}</div>
      <div className="text-15" style={{ wordBreak: 'break-word' }}>{empty ? '—' : value}</div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div className="col-12">
      <hr className="my-8" />
      <strong>{title}</strong>
    </div>
  );
}

function AdminRegistrations() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, unread: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/api/admin/registrations`, {
        ...auth(),
        params: { status, search, page, limit: PAGE_SIZE },
      });
      setRows(res.data.registrations || []);
      setCounts(res.data.counts || { all: 0, unread: 0 });
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(errText(err, 'Could not load registrations.'));
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setPage(1); }, [status, search]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };

  const setRead = async (id, read) => {
    try {
      await axios.post(`${apiUrl}/api/admin/registrations/${id}/read`, { read }, auth());
      setRows((cur) => cur.map((r) => (r.id === id ? { ...r, is_read: read } : r)));
      setDetail((cur) => (cur && cur.id === id ? { ...cur, is_read: read } : cur));
      notifySidebar();
      load();
    } catch (err) {
      setError(errText(err, 'Could not update the registration.'));
    }
  };

  const markAllRead = async () => {
    if (!window.confirm(`Mark all ${counts.unread} unread registrations as read?`)) return;
    try {
      const res = await axios.post(`${apiUrl}/api/admin/registrations/mark-all-read`, {}, auth());
      flash(`${res.data.marked} registrations marked as read.`);
      notifySidebar();
      load();
    } catch (err) {
      setError(errText(err, 'Could not mark all as read.'));
    }
  };

  const open = async (r) => {
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/api/admin/registrations/${r.id}`, auth());
      setDetail(res.data.registration);
      if (!r.is_read) await setRead(r.id, true); // opening an entry counts as reading it
    } catch (err) {
      setError(errText(err, 'Could not open the registration.'));
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tab = (id, label, count) => (
    <button type="button" className={`btn rounded-pill py-9 px-20 ${status === id ? 'btn-main' : 'btn-outline-main'}`} onClick={() => setStatus(id)}>
      {label} <span className="ms-4 opacity-75">({count})</span>
    </button>
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Registrations</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-outline-main rounded-pill py-9" disabled={counts.unread === 0} onClick={markAllRead}>
              <i className="ph ph-checks me-2" /> Mark all as read
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body d-flex flex-wrap gap-12 flex-between">
              <div className="d-flex gap-8 flex-wrap">
                {tab('all', 'All', counts.all)}
                {tab('unread', 'Unread', counts.unread)}
                {tab('read', 'Read', Math.max(0, counts.all - counts.unread))}
              </div>
              <input
                type="text"
                className="form-control py-9"
                style={{ maxWidth: 360 }}
                placeholder="Search name, email, mobile, course, country…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Website registrations ({total})</h5>
              <div className="text-13 text-gray-500">Submitted from ifmedu.in/apply. <strong>Bold</strong> rows are unread; open a row to read it.</div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center py-40 text-gray-400">{status === 'unread' ? 'You are all caught up - no unread registrations.' : 'No registrations found.'}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }} />
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Course interest</th>
                        <th>Country</th>
                        <th>Received</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.id}
                          onClick={() => open(r)}
                          style={{ cursor: 'pointer', fontWeight: r.is_read ? 400 : 700, background: r.is_read ? undefined : 'rgba(25, 135, 84, 0.07)' }}
                        >
                          <td>{!r.is_read && <span title="Unread" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#dc3545' }} />}</td>
                          <td>{r.first_name} {r.last_name}</td>
                          <td style={{ whiteSpace: 'normal' }}>
                            <div>{r.mobile}</div>
                            <div className="text-13" style={{ fontWeight: 400 }}>{r.email}</div>
                          </td>
                          <td style={{ maxWidth: 260, whiteSpace: 'normal' }}>{courseText(r)}</td>
                          <td>{r.country || '—'}</td>
                          <td>{formatDate(r.created_at || r.date)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-main py-6 px-10"
                              onClick={() => setRead(r.id, !r.is_read)}
                              title={r.is_read ? 'Mark as unread' : 'Mark as read'}
                            >
                              <i className={`ph ${r.is_read ? 'ph-envelope-simple' : 'ph-envelope-simple-open'} me-1`} />
                              {r.is_read ? 'Mark unread' : 'Mark read'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {pages > 1 && (
              <div className="card-footer d-flex flex-between align-items-center">
                <span className="text-13 text-gray-500">Page {page} of {pages}</span>
                <div className="d-flex gap-8">
                  <button type="button" className="btn btn-sm btn-outline-main" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                  <button type="button" className="btn btn-sm btn-outline-main" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
                </div>
              </div>
            )}
          </div>

          {detail && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Registration details"
              onClick={() => setDetail(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            >
              <div className="card" style={{ maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div className="card-header d-flex flex-between align-items-center">
                  <h5 className="mb-0">{detail.first_name} {detail.last_name}</h5>
                  <button type="button" className="btn btn-sm btn-outline-main" onClick={() => setDetail(null)}>Close</button>
                </div>
                <div className="card-body">
                  <div className="row g-16">
                    <Field label="Registration ID" value={`#${detail.id}`} />
                    <Field label="Status" value={detail.is_read ? 'Read' : 'Unread'} />
                    <Field label="First name" value={detail.first_name} />
                    <Field label="Last name" value={detail.last_name} />
                    <Field label="Mobile" value={detail.mobile} />
                    <Field label="Email" value={detail.email} />
                    <Field label="Other telephone" value={detail.main_tel} />
                    <Field label="Aadhaar number" value={detail.aadhar_no} />
                    <Field label="Country" value={detail.country} />
                    <Field label="Postcode" value={detail.postcode} />
                    <Field label="Address" value={detail.address} wide />
                    <Field label="Website" value={detail.website} wide />

                    <Section title="Education and course" />
                    <Field label="Course interest" value={(detail.courses || []).filter((c) => c && c !== 'NA').join(', ')} wide />
                    <Field label="Other course (typed in)" value={detail.course_other_text} wide />
                    <Field label="Has a degree" value={detail.degree} />
                    <Field label="Engineering background" value={detail.engineering} />

                    <Section title="Guardian" />
                    <Field label="First name" value={detail.guardian_first_name} />
                    <Field label="Last name" value={detail.guardian_last_name} />
                    <Field label="Job title" value={detail.guardian_job_title} />
                    <Field label="Mobile" value={detail.guardian_mobile} />
                    <Field label="Telephone" value={detail.guardian_telephone} />
                    <Field label="Email" value={detail.guardian_email} />

                    <Section title="Submission" />
                    <Field label="Date on the form" value={formatDate(detail.date)} />
                    <Field label="Received" value={detail.created_at ? new Date(detail.created_at).toLocaleString() : ''} />
                  </div>
                </div>
                <div className="card-footer d-flex gap-8 flex-wrap">
                  <button type="button" className="btn btn-outline-main rounded-pill" onClick={() => setRead(detail.id, !detail.is_read)}>
                    {detail.is_read ? 'Mark as unread' : 'Mark as read'}
                  </button>
                  {detail.mobile && <a className="btn btn-main rounded-pill" href={`https://wa.me/${String(detail.mobile).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">WhatsApp</a>}
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminRegistrations;
